/**
 * TwilioRequirementCallout — the "no surprises" trust primitive.
 *
 * Placed on every surface a prospect touches BEFORE they enter data:
 * landing hero, pricing page, signup form, and onboard wizard step 0.
 * Copy is deliberately blunt: "Yes, you need a Twilio account. Here's why,
 * here's what it costs you, here's exactly what we do to make it painless."
 * Better to lose the 1% who won't create a Twilio account at signup than
 * lose them at hour-3 when they realize and rage-cancel.
 */
import { Warning, Phone, CurrencyDollar, ArrowRight } from "@phosphor-icons/react";

export default function TwilioRequirementCallout({ variant = "full", className = "" }) {
  if (variant === "compact") {
    return (
      <div className={`p-3 rounded-lg bg-[#F5C543]/15 border border-[#F5C543]/50 text-xs text-[#121914] leading-snug ${className}`} data-testid="twilio-callout-compact">
        <b className="text-[#B45309]">Heads up:</b> you'll need a free <a href="https://www.twilio.com/try-twilio" target="_blank" rel="noreferrer" className="underline font-semibold">Twilio account</a> to receive calls & texts (~$1.15/mo for the phone number). We handle the setup paperwork inside the app — takes 5 min.
      </div>
    );
  }

  return (
    <div className={`paper-card p-5 bg-[#F5C543]/12 border-2 border-[#F5C543] ${className}`} data-testid="twilio-callout-full">
      <div className="flex items-start gap-3 mb-3">
        <div className="w-10 h-10 rounded-lg bg-[#F5C543] flex items-center justify-center flex-shrink-0">
          <Warning size={20} weight="fill" color="#121914" />
        </div>
        <div>
          <div className="overline text-[#B45309] mb-0.5">Before you sign up — read this</div>
          <div className="font-heading font-extrabold text-lg text-[#121914]">You'll need a Twilio account</div>
        </div>
      </div>
      <p className="text-sm text-[#121914] leading-relaxed mb-3">
        BuzzerAI is your <b>waitlist + AI receptionist</b>. But every restaurant needs its own <b>phone number and SMS delivery</b> — that piece can't be shared or resold cheaply, so we ask you to bring your own. It's called <a href="https://www.twilio.com/try-twilio" target="_blank" rel="noreferrer" className="underline font-semibold text-[#1E3A2F]">Twilio</a> and it's the same telecom backbone the big guys use.
      </p>
      <div className="grid sm:grid-cols-2 gap-3 mb-3">
        <div className="flex items-start gap-2">
          <CurrencyDollar size={16} weight="fill" color="#1E3A2F" className="mt-0.5 flex-shrink-0" />
          <div className="text-xs text-[#121914] leading-snug"><b>Cost:</b> ~$1.15/mo for a phone number + $0.0075 per SMS (~$3-8/mo for most restaurants). You pay Twilio directly. We take zero markup.</div>
        </div>
        <div className="flex items-start gap-2">
          <Phone size={16} weight="fill" color="#1E3A2F" className="mt-0.5 flex-shrink-0" />
          <div className="text-xs text-[#121914] leading-snug"><b>Setup time:</b> 5 min to make the account + paste 2 keys into our app. We auto-wire every webhook and handle the US SMS compliance paperwork with an in-app wizard.</div>
        </div>
      </div>
      <div className="text-xs text-[#5C6B61] leading-snug">
        <b className="text-[#1E3A2F]">Why we do it this way:</b> restaurants that own their Twilio number own their line if they ever leave us. Everyone else in this space resells you SMS at 2-4× markup and you can't take your number with you. This is the trade — 5 min upfront to save you from being locked in later.
      </div>
    </div>
  );
}
